import {
    Alert,
    Avatar, Button, Flex, FormInstance, Input, message, Modal, notification, Popover, Progress,
    Skeleton, Space, Steps, Tag,
    Typography
} from "antd";
import { useEffect, useId, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { deleteRow, getData } from "../../../actions/apiCall";
import FormSetup from "../../../components/Forms/FormSetup";
import RenderForm from "../../../components/Forms/Form";
import { AuditOutlined, EyeOutlined, CloudUploadOutlined, DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { getApplicantData } from "../../../actions/auth";
import { decryptData, encryptData } from "../../../actions/crypto";
import { getFuncParseValue } from "../../../constants/functions";
import dayjs from "dayjs";
import { postData, putData } from "../../../actions/developer";

const statusTags = {
    pending: { label: 'Pending', color: 'orange' },
    underReview: { label: 'Under Review', color: 'blue' },
    approved: { label: 'Approved', color: 'green' },
    rejected: { label: 'Rejected', color: 'red' }
};

const Documents = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const uniqueId = useId();

    const [steps, setSteps] = useState<any[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [formConfig, setFormConfig] = useState<any[]>([]);
    const [documentConfig, setDocumentConfig] = useState<any>(null);
    const [formInstance, setFormInstance] = useState<FormInstance>(null);
    const [userData, setUserData] = useState<any>(null);
    const [formData, setFormData] = useState<any>([]);
    const [statistics, setStatistics] = useState<any>(null);
    const [openForm, setOpenForm] = useState(false);
    const [view, setView] = useState(false);
    const [errors, setErrors] = useState([])

    useEffect(() => {
        fetchUser();
    }, []);

    const fetchUser = async () => {
        const user: any = await dispatch(getApplicantData());
        if (user) {
            setUserData(user);
            if (['uploading', 'ErrorFixing'].includes(userData?.doccumentStatus)) {
                const params = new URLSearchParams(location.search);
                const step = searchParams.get("step");
                params.set("step", step ? step : encryptData("0"));
                params.set("login", encryptData(user._id));
                navigate({ search: params.toString() });
            }
        } else {
            navigate("/login/candidate");
        }
    };

    useEffect(() => {
        const step = searchParams.get("step");
        setCurrentStep(parseInt(step ? decryptData(step) : "0", 10));
    }, [searchParams]);

    useEffect(() => {
        if (!userData || !['uploading', 'ErrorFixing'].includes(userData?.doccumentStatus)) return;

        loadFormTabs();
    }, [userData]);

    const loadFormTabs = async () => {
        const res = await dispatch(getData('/employee/doccuments'));
        if (res?.data) {
            const sortedTabs = res.data.sort((a, b) => a.order - b.order).map(item => ({ id: item._id, title: item.name }));
            setSteps(sortedTabs);
            const step = searchParams.get("step")

            loadForm(sortedTabs[step ? decryptData(step) : 0]?.id || sortedTabs[0]?.id);
        }
    };

    const loadForm = async (tabId: string) => {
        const res = await dispatch(getData('/employee/doccuments/form', { formTabs: tabId, submiter: userData._id }));
        if (res?.code === 200) {
            const { formList = [], formStats } = res.data;
            setFormConfig(formList);
            setStatistics(formStats);
            if (formList.length === 1) {
                fetchFormData(formList[0].formConfigs._id);
            }
        }
    };

    const fetchFormData = async (formId: string) => {
        const res = await dispatch(getData(`/form-data/for/submiter/${userData._id}/${formId}`));
        if (res?.code === 200) {
            setFormData(res.data || []);
        }
    };

    useEffect(() => {
        if (formInstance && formData.length) {
            const values = formData.reduce((acc, { formField, formFieldValue }) => {
                acc[formField] = dayjs(formFieldValue).isValid() && formFieldValue.includes("T") ? dayjs(formFieldValue) : formFieldValue;
                return acc;
            }, {});
            const formRejectedErrors = formData.filter(ele => ele.approverComment).map(ele => <div className="text-red-900">{ele?.formFieldLabel} : {ele.approverComment}</div>)
            setErrors(formRejectedErrors)
            formInstance.setFieldsValue(values);
        }
    }, [formInstance, formData]);

    useEffect(() => {
        if (documentConfig) {
            fetchFormData(documentConfig._id);
            setOpenForm(true);
        }
    }, [documentConfig]);

    const getFileData = (file) => {
        if (!formData || file.type !== 'file') return [];
        const fileEntry = formData.find(item => item.formField === file.name);
        if (!fileEntry) return [];
        return {
            _fileList: [{
                uid: uniqueId,
                name: fileEntry.formFieldValue?.split('/').pop(),
                status: 'done',
                url: `${process.env.FILE_URL}${fileEntry.formFieldValue}`,
            }]
        };
    };

    const onTabChange = (key: number) => {
        setCurrentStep(key);
        const params = new URLSearchParams(location.search);
        params.set("step", encryptData(key));
        navigate({ search: params.toString() });
        loadForm(steps[key]?.id);
    };

    const onAddForm = async (formConfigs, formTabs) => {
        const res = await dispatch(postData('/employee/doccuments/add/new-form', { formConfigs, formTabs, submiter: userData._id }));
        if (res?.code === 200) {
            loadFormTabs();
        }
    }

    const handleSave = async () => {
        const res = await dispatch(getData('/employee/doccuments/form', { formTabs: steps[currentStep].id, submiter: userData._id }));
        const stats = res?.data?.formStats;
        if (res?.code === 200 && stats?.isCompleted) {
            if (currentStep + 1 < steps.length) {
                onTabChange(currentStep + 1);
            } else {
                notification.success({
                    message: 'Thank you for submitting your information',
                    description: 'We are reviewing your documents. We will contact you for the next steps shortly.',
                });
                submitForm();
            }
        } else {
            message.error("Please complete all documents before proceeding to the next step.");
        }
    };

    const submitForm = async () => {
        try {
            const resData = await dispatch(putData('/form-data/for/submiter/form/submited', {}));
            if (resData) {
                fetchUser();
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            message.error("Failed to submit the form. Please try again.");
        }
    }

    const onUpdateName = async (name: string, formId: string) => {
        const res = await dispatch(putData(`/employee/doccuments/form-config/${formId}`, { name }));
        if (res) {
            message.success("Form name updated successfully.");
            loadFormTabs();
        } else {
            message.error("Failed to update form name.");
        }
    }

    const deleteForm = async (formId: string) => {
        try {
            Modal.confirm({
                title: "Are you sure you want to delete this form?",
                content: "This action cannot be undone.",
                onOk: async () => {
                    const res = await dispatch(deleteRow(`/employee/doccuments/form-config/${formId}`));
                    if (res.code === 200) {
                        message.success("Form deleted successfully.");
                        loadFormTabs();
                    } else {
                        message.error(res.message || "Failed to delete form.");
                    }
                },
            });
        } catch (error) {
            console.error("Error deleting form:", error);
            message.error("Failed to delete form. Please try again.");
        }
    }

    return (
        <div className="min-h-screen bg-[#e5e8f3]">
            <div className="bg-white fixed top-0 w-full z-10 shadow-md">
                <div className="flex justify-between items-center p-4">
                    <img src={require('./../../../assets/logo/logo.png')} alt="Logo" className="w-40" />
                    <div>{!userData ? <Skeleton.Input active size="small" style={{ width: 120 }} /> : `${userData.firstName} ${userData.lastName}`}</div>
                </div>
            </div>

            <div className="flex justify-center pt-25">
                <div className="container max-w-5xl w-full bg-white rounded p-4">
                    <Steps current={currentStep} items={steps} />
                    {formConfig.length === 0 ? (
                        <div className="space-y-4 mt-4">
                            <Skeleton active title paragraph={{ rows: 1 }} />
                            <Skeleton active paragraph={{ rows: 2 }} />
                            <Skeleton.Button active style={{ width: 120 }} />
                        </div>
                    ) : (
                        <div className="p-4">
                            {documentConfig && (
                                <FormSetup
                                    isView={view}
                                    config={{
                                        apiParams: { formDataform: documentConfig._id, formDataSubmiter: userData._id, fieldlabel: documentConfig.fields },
                                        api: () => documentConfig.routeName?.trim(),
                                        modalTitle: () => documentConfig.modelTitle,
                                        getFormColumns: () => [
                                            {
                                                type: 'custom',
                                                span: 24,
                                                formItem: () =>
                                                    errors.length > 0 && <Alert type="error" className="p-2 mt-2" closable={false} description={errors.map(ele => <>
                                                        {ele}
                                                    </>)} />
                                            },
                                            ...documentConfig.fields.map(field => ({
                                                ...field,
                                                commonProps: {
                                                    disabled: view || !!formData?.find(ele => ele.formField === field.name)?.isApproved,
                                                    ...getFileData(field),
                                                },
                                                formItem: field.type === 'custom' ? getFuncParseValue(field.rawData) : field.formItem
                                            }))],
                                        combo: documentConfig.combo,
                                        onRefresh: () => {
                                            onTabChange(currentStep)
                                        },
                                    }}
                                    open={openForm}
                                    onClose={() => {
                                        setOpenForm(false);
                                        setDocumentConfig(null);
                                        setView(false);
                                    }}
                                    form={setFormInstance}
                                />
                            )}

                            {/* Single Form */}
                            {formConfig.length === 1 && (
                                <RenderForm
                                    isView={false}
                                    onClose={() => { }}
                                    config={{
                                        apiParams: {
                                            formDataform: formConfig[0].formConfigs._id,
                                            formDataSubmiter: userData._id,
                                            fieldlabel: formConfig[0].formConfigs.fields
                                        },
                                        api: () => formConfig[0].formConfigs.routeName?.trim(),
                                        modalTitle: () => formConfig[0].formConfigs.modelTitle,
                                        getFormColumns: () => [
                                            {
                                                type: 'custom',
                                                span: 24,
                                                formItem: () =>
                                                    errors.length > 0 && <Alert type="error" className="p-2 mt-2" closable={false} description={errors.map(ele => <>
                                                        {ele}
                                                    </>)} />
                                            },
                                            ...formConfig[0].formConfigs.fields.map(field => ({
                                                ...field,
                                                commonProps: {
                                                    disabled: !!formData?.find(ele => ele.formField === field.name)?.isApproved,
                                                    ...getFileData(field),
                                                },
                                                formItem: field.type === 'custom' ? getFuncParseValue(field.rawData) : field.formItem
                                            }))],
                                        combo: formConfig[0].formConfigs.combo,
                                        onRefresh: handleSave
                                    }}
                                    setLoading={() => { }}
                                    id={null}
                                    previousPage={null}
                                    formInstance={setFormInstance}
                                />
                            )}

                            {/* Multiple Forms */}
                            {formConfig.length > 1 && (
                                <div className="mt-6 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div className="flex gap-4">
                                            {Object.entries(statusTags).map(([key, tag]) => (
                                                <div className="flex items-center gap-1" key={key}>
                                                    <Tag color={tag.color}>{tag.label}</Tag>
                                                    <span className="text-sm text-gray-600">{statistics?.[key]} Documents</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div>
                                            <div className="flex justify-between gap-4 text-sm text-gray-600">
                                                <span>{statistics?.percentageofCompletion}%</span>
                                                <span>{statistics?.underReview + statistics?.approved} of {statistics?.total} Completed</span>
                                            </div>
                                            <Progress percent={statistics?.percentageofCompletion} showInfo={false} strokeColor="#52c41a" />
                                        </div>
                                    </div>

                                    {formConfig.map(({ formConfigs }, index) => (
                                        <Flex justify="space-between" key={formConfigs.name + '_' + index} className="border p-4 rounded">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-blue-100 w-8 h-8 p-2 rounded-full flex items-center text-center">
                                                    <AuditOutlined className="text-blue-900 m-auto" />
                                                </div>
                                                {formConfigs?.isPrivate && <Typography.Text editable={{
                                                    onChange: (e) => {
                                                        onUpdateName(e, formConfigs._id);
                                                    }
                                                }} >
                                                    {formConfigs.name}
                                                </Typography.Text>}
                                                {!formConfigs?.isPrivate && <Typography.Text>{formConfigs.name}</Typography.Text>}
                                                {formConfigs?.required == 'Yes' && <span className="text-sm text-red-400">*Required</span>}
                                            </div>
                                            <Space>
                                                <Tag bordered={false} color={statusTags[statistics?.underReviewForm?.[formConfigs._id]]?.color}>
                                                    {statusTags[statistics?.underReviewForm?.[formConfigs._id]]?.label}
                                                </Tag>
                                                {['pending', 'rejected'].includes(statistics?.underReviewForm?.[formConfigs._id]) && <Button icon={<CloudUploadOutlined className="mt-1" />} onClick={() => setDocumentConfig(formConfigs)} />}
                                                {!['pending', 'rejected'].includes(statistics?.underReviewForm?.[formConfigs._id]) && <Button icon={<EyeOutlined className="mt-1" />} onClick={() => {
                                                    setView(true);
                                                    setDocumentConfig(formConfigs)
                                                }} />}
                                                {formConfigs?.isPrivate && ['pending', 'rejected'].includes(statistics?.underReviewForm?.[formConfigs._id]) && <Button danger icon={<DeleteOutlined className="mt-1" />} onClick={() => deleteForm(formConfigs._id)} />}
                                            </Space>
                                        </Flex>
                                    ))}

                                    {formConfig.filter(ele => ele?.formConfigs?.addMore == 'Yes').map(({ formConfigs, formTabs }, index) => (
                                        <Flex justify="space-between" key={formConfigs.name + '_' + index} className="border p-4 rounded">
                                            <div className="flex items-center gap-2 text-blue-800">
                                                <div className="bg-blue-100 w-8 h-8 p-2 rounded-full flex items-center text-center">
                                                    <PlusCircleOutlined className="text-blue-900 m-auto" />
                                                </div>
                                                Add more {formConfigs.name}
                                            </div>
                                            <Space align="center">
                                                <Button type="default" onClick={() => onAddForm(formConfigs, formTabs)} >
                                                    Add more
                                                </Button>
                                            </Space>
                                        </Flex>
                                    ))}

                                </div>
                            )}

                            {/* Footer */}
                            <div className="flex justify-end gap-4 mt-6">
                                {currentStep > 0 && <Button onClick={() => onTabChange(currentStep - 1)}>Previous</Button>}
                                <Button type="primary" onClick={() => formConfig.length > 1 ? handleSave() : formInstance?.submit()}>
                                    Save & Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default Documents;
